/**
 * @file wordpressService.ts
 * @description Service for handling WordPress publishing API calls.
 */

import config from "../config/config";
import { auth } from "../config/firebaseConfig";
import log from "../config/logging";
import { WordPressPublishResponse } from "../types";

/**
 * Publish a single content to WordPress.
 * @param contentDetailId - ID of the content detail to publish
 * @param websiteId - Optional website ID (if not provided, will try to find by URL)
 * @returns WordPress publish response
 */
export const publishContent = async (
  contentDetailId: string,
  websiteId?: string
): Promise<WordPressPublishResponse> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${config.backendUrl}/api/wordpress/publish/${contentDetailId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ websiteId }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to publish content: ${response.statusText}`
      );
    }

    const data = await response.json();
    return {
      success: data.success,
      postId: data.postId,
      link: data.link,
      editUrl: data.editUrl,
    };
  } catch (error) {
    log.error("Error publishing content to WordPress", { error });
    throw error;
  }
};

/**
 * Publish all contents of a plan to WordPress (batch).
 * @param planId - ID of the plan
 * @returns Response with results for each content
 */
export const publishPlanContents = async (
  planId: string
): Promise<{
  success: boolean;
  message: string;
  results: Array<{
    contentDetailId: string;
    success: boolean;
    postId?: number;
    link?: string;
    editUrl?: string;
    error?: string;
  }>;
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}> => {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (!token) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(
      `${config.backendUrl}/api/wordpress/publish/plan/${planId}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to publish plan contents: ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    log.error("Error publishing plan contents to WordPress", { error });
    throw error;
  }
};
